import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { BlockCard, FarmRegionEditor, OrchardMap, SatellitePanel } from '../../components/orchard';
import { Maximize2, Plus, Edit, Trash2, Loader2, Layers, MapPin, Info, AlertTriangle, Archive, CheckCircle2 } from 'lucide-react';
import orchardService from '../../services/orchard';
import type { FarmAssetSummary, FarmMapData, FarmReleased } from '../../types/orchard';
import { ApiError } from '../../types/api';
import { errorMessage } from '../../utils/apiResponse';

/** "2 bridges", "1 soil sensor"… — only what there is. */
function releaseItems(r: FarmReleased): string[] {
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
  const items: string[] = [];
  if (r.controllers > 0) items.push(plural(r.controllers, 'bridge / controller', 'bridges / controllers'));
  if (r.sensors > 0) items.push(plural(r.sensors, 'soil sensor', 'soil sensors'));
  if (r.network_links > 0) items.push(plural(r.network_links, 'valve or pump link on the network map', 'valve and pump links on the network map'));
  return items;
}

function FarmDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orgIdParam = searchParams.get('org_id');
  const targetOrgId = orgIdParam ? parseInt(orgIdParam, 10) : undefined;
  const [mapData, setMapData] = useState<FarmMapData | null>(null);
  const [assetSummary, setAssetSummary] = useState<FarmAssetSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Why the server refused the delete — sprays, jobs, equipment still on the farm.
  const [deleteBlocked, setDeleteBlocked] = useState<string | null>(null);
  const [archiving, setArchiving] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  // What archiving will let go of, fetched when the confirmation opens.
  const [archivePreview, setArchivePreview] = useState<FarmAssetSummary | null>(null);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  // What the archive actually let go of, said once it is done.
  const [released, setReleased] = useState<FarmReleased | null>(null);

  const farmId = Number(id);

  const loadMapData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await orchardService.getFarmMapData(farmId, targetOrgId);
      if (response.success && response.data) {
        setMapData(response.data);
        if (response.data.farm.active === false) {
          const summary = await orchardService.getFarmAssetSummary(farmId);
          if (summary.success && summary.data) setAssetSummary(summary.data);
        }
      } else {
        setError('Failed to load farm');
      }
    } catch {
      setError('Failed to load farm. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [farmId, targetOrgId]);

  useEffect(() => {
    loadMapData();
  }, [loadMapData]);

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteBlocked(null);
    try {
      const response = await orchardService.deleteFarm(farmId);
      if (response.success) {
        navigate('/orchard');
      } else if (response.error.code === 'FARM_IN_USE') {
        setDeleteBlocked(response.error.message);
      } else {
        setError(errorMessage(response, 'Failed to delete farm'));
        setShowDeleteConfirm(false);
      }
    } catch (e) {
      // A farm with history cannot go; the server says what is in the way, and
      // archiving is the answer to offer rather than a dead end.
      if (e instanceof ApiError && e.code === 'FARM_IN_USE') {
        setDeleteBlocked(e.message);
      } else {
        setError(errorMessage(e, 'Failed to delete farm. Please try again.'));
        setShowDeleteConfirm(false);
      }
    } finally {
      setDeleting(false);
    }
  };

  const openArchive = () => {
    setShowDeleteConfirm(false);
    setShowArchiveConfirm(true);
    setArchiveError(null);
    setArchivePreview(null);
    orchardService.getFarmAssetSummary(farmId)
      .then((res) => { if (res.success) setArchivePreview(res.data); })
      // The preview is a courtesy; archiving works without it.
      .catch(() => {});
  };

  const handleArchive = async () => {
    if (!mapData) return;
    setArchiving(true);
    setArchiveError(null);
    try {
      const { farm } = mapData;
      // The farm PUT validates name and boundary as required, so they ride along unchanged.
      const res = await orchardService.updateFarm(farmId, { name: farm.name, boundary_geometry: farm.boundary_geometry, active: false });
      if (!res.success) throw new Error(res.error.message);
      setReleased(res.data.released ?? null);
      setShowArchiveConfirm(false);
      setDeleteBlocked(null);
      await loadMapData();
    } catch (e) {
      setArchiveError(errorMessage(e, 'Could not archive the farm'));
    } finally {
      setArchiving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Farm"
          subtitle="Loading..."
          icon={<Maximize2 className="h-6 w-6 text-white" />}
          showBackButton
        />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-10 w-10 text-primary-600 animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !mapData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Farm"
          subtitle="Error loading farm"
          icon={<Maximize2 className="h-6 w-6 text-white" />}
          showBackButton
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-error-50 border border-error-200 rounded-xl p-6 text-center">
            <p className="text-error-700 mb-4">{error || 'Farm not found'}</p>
            <Button variant="outline" onClick={loadMapData}>Try Again</Button>
          </div>
        </div>
      </div>
    );
  }

  const { farm, blocks, cultivar_areas } = mapData;
  const totalTrees = cultivar_areas.reduce((sum, a) => sum + (a.tree_count ?? 0), 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title={farm.name}
        subtitle={[farm.code && `Code ${farm.code}`, farm.location || 'Farm Details'].filter(Boolean).join(' · ')}
        icon={<Maximize2 className="h-6 w-6 text-white" />}
        showBackButton
        actionButtons={
          <div className="flex gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate(`/orchard/blocks/new?farm_id=${farm.id}${targetOrgId ? `&org_id=${targetOrgId}` : ''}`)}
            >
              <Plus className="h-4 w-4" />
              New Block
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/orchard/farms/${farm.id}/edit`)}
            >
              <Edit className="h-4 w-4" />
              Edit
            </Button>
            {farm.active !== false && !targetOrgId && (
              <Button variant="outline" size="sm" aria-label="Archive farm" onClick={openArchive}>
                <Archive className="h-4 w-4" />
                <span className="hidden sm:inline">Archive</span>
              </Button>
            )}
            <Button
              variant="danger"
              size="sm"
              aria-label="Delete farm"
              onClick={() => { setShowArchiveConfirm(false); setShowDeleteConfirm(true); }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        }
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {error && (
          <div className="bg-error-50 border border-error-200 rounded-lg p-4">
            <p className="text-error-700">{error}</p>
          </div>
        )}

        {farm.active === false && (
          <div className="bg-amber-50 border border-amber-300 rounded-xl p-5">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-base font-semibold text-amber-900 mb-1">Farm is inactive</h3>
                <p className="text-sm text-amber-800 mb-3">
                  This farm is hidden from dropdowns and listings. New blocks, cultivar areas,
                  irrigation network items, weather stations, and scenarios cannot be added until it is reactivated.
                </p>
                {assetSummary && (
                  <>
                    <p className="text-sm font-medium text-amber-900 mb-2">Currently attached:</p>
                    <ul className="text-sm text-amber-800 space-y-1 mb-3">
                      <li>{assetSummary.blocks} blocks · {assetSummary.cultivar_areas} cultivar areas</li>
                      {assetSummary.equipment > 0 && (
                        <li>
                          {assetSummary.equipment} equipment item{assetSummary.equipment === 1 ? '' : 's'} —{' '}
                          <button className="underline" onClick={() => navigate(`/equipment?farm_id=${farm.id}`)}>
                            view & reassign
                          </button>
                        </li>
                      )}
                      {assetSummary.weather_stations > 0 && (
                        <li>
                          {assetSummary.weather_stations} weather station{assetSummary.weather_stations === 1 ? '' : 's'} —{' '}
                          <button className="underline" onClick={() => navigate('/weather-stations/setup')}>
                            view & reassign
                          </button>
                        </li>
                      )}
                      {assetSummary.chemical_locations > 0 && (
                        <li>
                          {assetSummary.chemical_locations} chemical location{assetSummary.chemical_locations === 1 ? '' : 's'} —{' '}
                          <button className="underline" onClick={() => navigate('/spray/inventory')}>
                            view & reassign
                          </button>
                        </li>
                      )}
                    </ul>
                  </>
                )}
                <Button variant="outline" size="sm" onClick={() => navigate(`/orchard/farms/${farm.id}/edit`)}>
                  <Edit className="h-4 w-4" />
                  Edit farm to reactivate
                </Button>
              </div>
            </div>
          </div>
        )}

        {released && (
          <div className="bg-success-50 border border-success-200 rounded-xl p-4 flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-success-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-success-800 min-w-0">
              <p className="font-medium">Farm archived.</p>
              {releaseItems(released).length > 0 ? (
                <p>
                  Released {releaseItems(released).join(', ')}. Bridges and sensors are under Unassigned on{' '}
                  <button className="underline" onClick={() => navigate('/irrigation')}>Irrigation</button>, ready for another farm.
                </p>
              ) : (
                <p>It had no irrigation hardware to release.</p>
              )}
            </div>
          </div>
        )}

        {/* Archive Confirmation */}
        {showArchiveConfirm && (
          <div className="bg-amber-50 border border-amber-300 rounded-xl p-6">
            <h3 className="text-base font-semibold text-amber-900 mb-2">Archive farm?</h3>
            <p className="text-sm text-amber-800 mb-3">
              <strong className="break-words">{farm.name}</strong> will be hidden from lists and pickers. Its blocks,
              records and irrigation map are kept, and it can be reactivated from Edit.
            </p>
            {archivePreview && releaseItems(archivePreview).length > 0 && (
              <>
                <p className="text-sm font-medium text-amber-900 mb-1">Its irrigation hardware is released for use on another farm:</p>
                <ul className="list-disc pl-5 text-sm text-amber-800 mb-3 space-y-0.5">
                  {releaseItems(archivePreview).map((line) => <li key={line}>{line}</li>)}
                </ul>
                <p className="text-xs text-amber-700 mb-3">
                  Programs on the bridges keep running — stop them on Irrigation if the site should stop watering.
                  Reactivating the farm does not reattach anything.
                </p>
              </>
            )}
            {archiveError && <p className="text-sm text-error-700 mb-3">{archiveError}</p>}
            <div className="flex flex-wrap gap-3">
              <Button variant="primary" isLoading={archiving} onClick={handleArchive}>
                Archive farm
              </Button>
              <Button variant="outline" onClick={() => setShowArchiveConfirm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Delete Confirmation */}
        {showDeleteConfirm && (
          <div className="bg-error-50 border border-error-300 rounded-xl p-6">
            <h3 className="text-base font-semibold text-error-900 mb-2">Delete Farm?</h3>
            <p className="text-sm text-error-700 mb-4">
              This will permanently delete <strong>{farm.name}</strong> and all its blocks and cultivar areas.
              This action cannot be undone.
            </p>
            {deleteBlocked ? (
              <>
                <p className="text-sm text-error-800 font-medium mb-2">{deleteBlocked}</p>
                <p className="text-sm text-error-700 mb-4">
                  Archiving hides the farm from lists and pickers and keeps its records. It can be reactivated from Edit.
                </p>
                <div className="flex flex-wrap gap-3">
                  {farm.active !== false && (
                    <Button variant="primary" onClick={openArchive}>
                      Archive farm…
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => { setShowDeleteConfirm(false); setDeleteBlocked(null); }}>
                    Cancel
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex flex-wrap gap-3">
                <Button variant="danger" isLoading={deleting} onClick={handleDelete}>
                  Yes, Delete Farm
                </Button>
                <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                  Cancel
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-neutral-200 p-4 text-center shadow-sm">
            <p className="text-2xl font-bold text-neutral-900">
              {farm.total_area_hectares != null ? Number(farm.total_area_hectares).toFixed(1) : '—'}
            </p>
            <p className="text-xs text-neutral-500 mt-1">Hectares</p>
          </div>
          <div className="bg-white rounded-xl border border-neutral-200 p-4 text-center shadow-sm">
            <p className="text-2xl font-bold text-primary-600">{blocks.length}</p>
            <p className="text-xs text-neutral-500 mt-1">Blocks</p>
          </div>
          <div className="bg-white rounded-xl border border-neutral-200 p-4 text-center shadow-sm">
            <p className="text-2xl font-bold text-success-600">{cultivar_areas.length}</p>
            <p className="text-xs text-neutral-500 mt-1">Cultivar Areas</p>
          </div>
          <div className="bg-white rounded-xl border border-neutral-200 p-4 text-center shadow-sm">
            <p className="text-2xl font-bold text-purple-600">{totalTrees.toLocaleString()}</p>
            <p className="text-xs text-neutral-500 mt-1">Total Trees</p>
          </div>
        </div>

        {/* Map */}
        <div className="rounded-xl overflow-hidden border border-neutral-200 shadow-sm" style={{ height: '480px' }}>
          <OrchardMap
            farmBoundary={farm.boundary_geometry}
            blocks={blocks}
            cultivarAreas={cultivar_areas}
            selectedEntityType="farm"
            center={
              farm.center_point
                ? [farm.center_point.lat, farm.center_point.lng]
                : undefined
            }
            zoom={15}
          />
        </div>

        {/* Farm Info — always rendered. It used to be gated on one of these
            three being present, which hid the card exactly when it had the most
            to say: a farm with nothing filled in is the one whose missing
            growing region needs explaining. */}
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Info className="h-4 w-4 text-neutral-500" />
              <h3 className="text-sm font-semibold text-neutral-700">Farm Information</h3>
            </div>
            {farm.location && (
              <div className="flex items-center gap-2 text-sm text-neutral-600 mb-2">
                <MapPin className="h-4 w-4 text-neutral-400 flex-shrink-0" />
                <span>{farm.location}</span>
              </div>
            )}
            {/* Evaluations recorded here inherit it, so a blank one explains
                why this farm's maturity dates are not reaching the cultivar —
                and it can be set right here. */}
            <FarmRegionEditor farm={farm} onSaved={loadMapData} readOnly={targetOrgId != null} />
            {farm.description && (
              <p className="text-sm text-neutral-600">{farm.description}</p>
            )}
          </CardContent>
        </Card>

        {/* Sentinel-2 NDVI / near-infrared. Not on another org's farm viewed by a
            super admin: the imagery routes read the signed-in org. */}
        {targetOrgId == null && farm.boundary_geometry && (
          <SatellitePanel
            farmId={farm.id}
            farmName={farm.name}
            target={{ type: 'farm', id: farm.id, name: farm.name, boundary: farm.boundary_geometry }}
            outlines={blocks.map((b) => b.boundary_geometry)}
          />
        )}

        {/* Blocks */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Layers className="h-5 w-5 text-primary-600" />
              <h2 className="text-lg font-semibold text-neutral-900">Blocks</h2>
              <span className="text-sm text-neutral-500">({blocks.length})</span>
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate(`/orchard/blocks/new?farm_id=${farm.id}${targetOrgId ? `&org_id=${targetOrgId}` : ''}`)}
            >
              <Plus className="h-4 w-4" />
              Add Block
            </Button>
          </div>

          {blocks.length === 0 ? (
            <div className="bg-white rounded-xl border border-neutral-200 p-10 text-center">
              <div className="h-12 w-12 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-3">
                <Layers className="h-6 w-6 text-neutral-400" />
              </div>
              <h3 className="text-base font-semibold text-neutral-900 mb-1">No Blocks Yet</h3>
              <p className="text-sm text-neutral-600 mb-4">
                Add blocks to divide your farm into management areas.
              </p>
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate(`/orchard/blocks/new?farm_id=${farm.id}${targetOrgId ? `&org_id=${targetOrgId}` : ''}`)}
              >
                <Plus className="h-4 w-4" />
                Create First Block
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {blocks.map((block) => (
                <BlockCard key={block.id} block={block} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default FarmDetail;
